/** /pair?code=… (STORY-073): the phone claims a pairing code from the teacher's computer on
 * the room network and gets a controller token for that local host. */
import { useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { APP_BASE } from "../api/base";
import { keepTokens } from "../auth/session";
import { product } from "../product";
import { claimPairing } from "../teacher/api";
import { Button } from "../ui";

export function Pair() {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as { code?: string };
  const [code, setCode] = useState(search.code ?? "");
  const [state, setState] = useState<"idle" | "busy" | "done" | "failed">("idle");

  async function claim(c: string) {
    setState("busy");
    try {
      const r = await claimPairing(c.trim().toUpperCase());
      keepTokens(r.access_token, null, true);
      setState("done");
      // A full load, not the router: the tokens were just written and every reader of the
      // auth state must start from them. Under the mount prefix, so it stays inside the app.
      window.location.assign(`${APP_BASE}/t`);
    } catch {
      setState("failed");
    }
  }
  useEffect(() => {
    if (search.code && state === "idle") void claim(search.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.code]);

  return (
    <main className="font-system mx-auto flex min-h-dvh max-w-md flex-col gap-4 p-6">
      <p className="text-lg font-bold tracking-tight" translate="no">{product.name}</p>
      <h1 className="text-2xl font-bold">{t("pair.title")}</h1>
      <p className="text-[color:var(--ink-2)]">{state === "busy" ? t("pair.body") : t("pair.offlineNote")}</p>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void claim(code);
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t("pair.code")}</span>
          <input className="field text-2xl tracking-widest" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoCapitalize="characters" autoComplete="off" maxLength={6} />
        </label>
        <Button type="submit" loading={state === "busy"} disabled={code.trim().length < 4} block>{t("pair.cta")}</Button>
      </form>
      {state === "done" && <p role="status">{t("pair.done")}</p>}
      {state === "failed" && <p role="alert" style={{ color: "var(--danger)" }}>{t("pair.failed")}</p>}
    </main>
  );
}
